import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  EnviarModeloEntrada,
  MensagemDto,
  ModeloOficialDto,
  NovoNumeroOficialEntrada,
  NumeroNaApiOficialDto,
  PrecosOficiaisEntrada,
  Provedor,
  SalvarContaOficialEntrada,
} from '@crm/shared'
import { api } from './cliente'
import { chavesChat, invalidarChat } from './hooks-chat'

/** API oficial do WhatsApp (ADR 0033): a aba de Números e os modelos na conversa. */

export const chavesApiOficial = {
  numeros: ['api-oficial', 'numeros'] as const,
  modelosDaConversa: (chamadoId: string) => ['api-oficial', 'modelos', chamadoId] as const,
}

interface ResultadoDaConferencia {
  ok: boolean
  erro: string | null
}

export function useNumerosNaApiOficial() {
  return useQuery({
    queryKey: chavesApiOficial.numeros,
    queryFn: () => api<NumeroNaApiOficialDto[]>('/api/api-oficial/numeros'),
  })
}

/** Mexer na conta muda a aba e a lista de números (provedor, status). */
function useInvalidarNumeros() {
  const cliente = useQueryClient()
  return () => {
    void cliente.invalidateQueries({ queryKey: chavesApiOficial.numeros })
    void cliente.invalidateQueries({ queryKey: chavesChat.conexoes })
  }
}

export function useSalvarContaOficial() {
  const invalidar = useInvalidarNumeros()
  return useMutation({
    mutationFn: ({ conexaoId, dados }: { conexaoId: string; dados: SalvarContaOficialEntrada }) =>
      api<ResultadoDaConferencia>(`/api/api-oficial/numeros/${conexaoId}/conta`, {
        metodo: 'PUT',
        corpo: dados,
      }),
    onSuccess: invalidar,
  })
}

export function useNovoNumeroOficial() {
  const invalidar = useInvalidarNumeros()
  return useMutation({
    mutationFn: (dados: NovoNumeroOficialEntrada) =>
      api<ResultadoDaConferencia & { conexaoId: string }>('/api/api-oficial/numeros', {
        metodo: 'POST',
        corpo: dados,
      }),
    onSuccess: invalidar,
  })
}

export function useConferirContaOficial() {
  const invalidar = useInvalidarNumeros()
  return useMutation({
    mutationFn: (conexaoId: string) =>
      api<ResultadoDaConferencia>(`/api/api-oficial/numeros/${conexaoId}/conferir`, {
        metodo: 'POST',
      }),
    onSettled: invalidar,
  })
}

export function useSincronizarModelos() {
  const invalidar = useInvalidarNumeros()
  return useMutation({
    mutationFn: (conexaoId: string) =>
      api<{ quantidade: number }>(`/api/api-oficial/numeros/${conexaoId}/modelos`, {
        metodo: 'POST',
      }),
    onSuccess: invalidar,
  })
}

export function useSalvarPrecosOficiais() {
  const invalidar = useInvalidarNumeros()
  return useMutation({
    mutationFn: ({ conexaoId, precos }: { conexaoId: string; precos: PrecosOficiaisEntrada }) =>
      api<{ ok: true }>(`/api/api-oficial/numeros/${conexaoId}/precos`, {
        metodo: 'PUT',
        corpo: precos,
      }),
    onSuccess: invalidar,
  })
}

export function useTrocarProvedor() {
  const invalidar = useInvalidarNumeros()
  return useMutation({
    mutationFn: ({ conexaoId, provedor }: { conexaoId: string; provedor: Provedor }) =>
      api<{ provedor: Provedor; status: string }>(
        `/api/api-oficial/numeros/${conexaoId}/provedor`,
        {
          metodo: 'PUT',
          corpo: { provedor },
        },
      ),
    onSuccess: invalidar,
  })
}

export function useApagarContaOficial() {
  const invalidar = useInvalidarNumeros()
  return useMutation({
    mutationFn: (conexaoId: string) =>
      api<{ ok: true }>(`/api/api-oficial/numeros/${conexaoId}/conta`, { metodo: 'DELETE' }),
    onSuccess: invalidar,
  })
}

// ─────────────────────────── na conversa ───────────────────────────

/** Os modelos aprovados do número desta conversa. Vazio na Evolution. */
export function useModelosDaConversa(chamadoId: string | null, habilitado: boolean) {
  return useQuery({
    queryKey: chavesApiOficial.modelosDaConversa(chamadoId ?? ''),
    queryFn: () => api<ModeloOficialDto[]>(`/api/chamados/${chamadoId}/modelos`),
    enabled: Boolean(chamadoId) && habilitado,
    staleTime: 60_000,
  })
}

export function useEnviarModelo() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ chamadoId, dados }: { chamadoId: string; dados: EnviarModeloEntrada }) =>
      api<MensagemDto>(`/api/chamados/${chamadoId}/modelo`, { metodo: 'POST', corpo: dados }),
    onSuccess: (_m, { chamadoId }) => invalidarChat(cliente, chamadoId),
  })
}
